const SMALL_WORDS = new Set([
	"a", "do", "dla", "i", "lub", "na", "od", "po", "przy", "w", "z", "za", "ze",
]);

const GENERIC_NAMES = new Set([
	"asset", "banner", "file", "grafika", "graphic", "group", "hero", "icon", "ikona",
	"illustration", "image", "img", "layer", "logo", "photo", "picture", "rectangle",
	"shape", "thumb", "thumbnail",
]);

const FORM_TOKEN_MAP = new Map([
	["dn1", "DN-1"],
	["dt1", "DT-1"],
	["in1", "IN-1"],
	["zin1", "ZIN-1"],
	["zin2", "ZIN-2"],
	["zin3", "ZIN-3"],
	["pesel", "PESEL"],
	["epuap", "ePUAP"],
]);

function safeDecode(value) {
	try {
		return decodeURIComponent(value);
	} catch {
		return value;
	}
}

export function getImageFilename(src) {
	const value = String(src || "").trim();

	if (!value) {
		return "";
	}

	const clean = value.split("?")[0].split("#")[0];
	const decoded = safeDecode(clean);
	return decoded.split("/").filter(Boolean).pop() || "";
}

export function getImageStem(src) {
	return getImageFilename(src).replace(/\.[^.]+$/, "");
}

function stripTechnicalAffixes(value) {
	return String(value || "")
		.replace(/^\d{10,17}[-_ ]+/, "")
		.replace(/@(?:2x|3x)$/i, "")
		.replace(/[-_ ](?:0?\d{2,4})$/, "")
		.trim();
}

function normalizedStem(src) {
	return stripTechnicalAffixes(getImageStem(src))
		.toLowerCase()
		.replace(/[-_ ]+/g, " ")
		.trim();
}

export function isTechnicalImageFilename(src) {
	const rawStem = getImageStem(src).trim();
	const technicalStem = stripTechnicalAffixes(rawStem);
	const normalized = technicalStem
		.toLowerCase()
		.replace(/[-_ ]+/g, " ")
		.trim();

	if (!rawStem || !technicalStem || !normalized) {
		return true;
	}

	if (
		/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
			normalized,
		)
	) {
		return true;
	}

	if (/^[0-9a-f]{16,}$/i.test(normalized)) {
		return true;
	}

	if (/^(?:group|frame|layer|rectangle|shape)[-_ ]*\d+(?:[-_ ]+\d+)*$/i.test(technicalStem)) {
		return true;
	}

	if (/^(?:img|dsc|pxl|screenshot|screen[-_ ]?shot|zrzut[-_ ]?ekranu)[-_ ]*\d/i.test(technicalStem)) {
		return true;
	}

	if (/^post[-_ ]*\d+(?:[-_ ]*\d+)*$/i.test(technicalStem)) {
		return true;
	}

	if (/^article[-_ ]*image[-_ ]*\d+$/i.test(technicalStem)) {
		return true;
	}

	if (/^ezgif[-_ ]/i.test(technicalStem)) {
		return true;
	}

	if (/^\d+$/.test(normalized)) {
		return true;
	}

	return GENERIC_NAMES.has(normalized);
}

function formatWord(word, index) {
	const compact = String(word || "").toLowerCase().replace(/[^a-z0-9ąćęłńóśźż]/gi, "");
	const mapped = FORM_TOKEN_MAP.get(compact);

	if (mapped) {
		return mapped;
	}

	if (index > 0 && SMALL_WORDS.has(compact)) {
		return compact;
	}

	if (!word) {
		return "";
	}

	return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
}

export function altFromFilename(src) {
	if (isTechnicalImageFilename(src)) {
		return "";
	}

	let stem = stripTechnicalAffixes(getImageStem(src));

	if (!stem) {
		return "";
	}

	stem = stem
		.replace(/@(?:2x|3x)$/i, "")
		.replace(/[-_]+/g, " ")
		.replace(/\s+/g, " ")
		.trim();

	const words = stem.split(" ").filter(Boolean);
	const formatted = words.map(formatWord).filter(Boolean).join(" ");

	return formatted
		.replace(/\bPrzyjazne deklaracje\b/gi, "Przyjazne Deklaracje")
		.replace(/\s+/g, " ")
		.trim();
}

function normalizeComparable(value) {
	return stripTechnicalAffixes(String(value || "").replace(/\.[^.]+$/, ""))
		.toLowerCase()
		.replace(/[-_ ]+/g, " ")
		.trim();
}

export function isFilenameLikeAlt(alt, src) {
	const value = String(alt || "").trim();

	if (!value) {
		return false;
	}

	return normalizeComparable(value) === normalizeComparable(getImageStem(src));
}

export function suggestImageAlt({ src, alt } = {}) {
	const explicit = String(alt ?? "").trim();

	if (explicit && !isFilenameLikeAlt(explicit, src)) {
		return explicit;
	}

	return altFromFilename(src);
}

export function resolveImageAlt({ src, alt, decorative = false } = {}) {
	if (decorative) {
		return "";
	}

	if (alt !== undefined && alt !== null) {
		return String(alt).trim();
	}

	return altFromFilename(src);
}
